import type { LinksFunction } from "@remix-run/node"

import * as Icon from "@iyk/icons"
import * as UI from "@iyk/ui"

import {
  GoogleAnalyticsTracking,
  LinkedInTracking,
  MetaPixelTracking,
  MixpanelTracking,
  PostHogTracking,
  TikTokTracking,
} from "@iyk/tracking"
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  useLoaderData,
  useRouteError,
  type MetaFunction,
  type ShouldRevalidateFunction,
} from "@remix-run/react"
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix"
import { Effect, Option } from "effect"
import { LoaderResponse } from "../../../../packages/remix/src/index.ts"
import { TrackingProvider } from "../marketing/tracking-provider.tsx"
import { ClientScript } from "../observability/Sentry.tsx"
import { IYK_MAIL } from "../ui/links.ts"
import { Remix } from "./main.server.ts"
import { getHtmlTitle } from "./meta.ts"

import "../ui/styles.css"

export const shouldRevalidate: ShouldRevalidateFunction = () => {
  return false
}

export const links: LinksFunction = () => [
  ...UI.favicons,
  {
    rel: "manifest",
    href: "/site.webmanifest",
  },
]

export const meta: MetaFunction = () => {
  const title = getHtmlTitle()
  const description = "A cyberphysical card that holds a personalized video message"
  const url = "https://postcards.iyk.app"
  const image = "https://assets.iyk-dev.app/postcards/iyk-postcards-og-image.jpg"

  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: url },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
  ]
}

export const loader = Remix.loader.handler(({ capabilities: { sentryConfig, marketing } }) =>
  Effect.succeed(
    LoaderResponse.Ok({
      sentryConfig: Option.getOrNull(sentryConfig),
      linkedInId: Option.getOrNull(marketing.linkedInId),
      googleAnalyticsId: Option.getOrNull(marketing.googleAnalyticsId),
      metaPixelId: Option.getOrNull(marketing.metaPixelId),
      mixpanelId: Option.getOrNull(marketing.mixpanelId),
      postHogId: Option.getOrNull(marketing.postHogId),
      tiktokPixelId: Option.getOrNull(marketing.tiktokPixelId),
    }),
  ),
)

const App = () => {
  const {
    googleAnalyticsId,
    linkedInId,
    metaPixelId,
    mixpanelId,
    postHogId,
    sentryConfig,
    tiktokPixelId,
  } = useLoaderData<typeof loader>()

  return (
    <html lang="en-US" dir="ltr" className="light bg-background text-gray-12">
      <head>
        <meta name="viewport" content="width=device-width" />
        <Meta />
        <Links />
        <GoogleAnalyticsTracking id={googleAnalyticsId} />
        <MetaPixelTracking id={metaPixelId} />
        <MixpanelTracking id={mixpanelId} track_pageview="url-with-path-and-query-string" />
        <PostHogTracking
          id={postHogId}
          api_host="https://us.i.posthog.com"
          person_profiles="identified_only"
          enable_heatmaps
        />
        <TikTokTracking id={tiktokPixelId} />
        <LinkedInTracking id={linkedInId} />
      </head>
      <body>
        <TrackingProvider>
          <Outlet />
          <UI.Toaster />
        </TrackingProvider>
        <Scripts />
        {sentryConfig && (
          <ClientScript dsn={sentryConfig.dsn} environment={sentryConfig.environment} />
        )}
      </body>
    </html>
  )
}

export default withSentry(App)

export const ErrorBoundary = () => {
  const error = useRouteError()

  captureRemixErrorBoundaryError(error)

  return (
    <html lang="en">
      <head>
        <Meta />
        <Links />
      </head>
      <body>
        <UI.Pixelation className="flex flex-col justify-between">
          <Icon.IykLogo className="size-7" />

          <div className="flex flex-col items-center gap-6">
            <Icon.InfoCircled />

            <PostcardError />
          </div>

          <p className="text-center text-balance px-4">
            If you believe this is an error, please contact us at{" "}
            <a className="underline" href={`mailto:${IYK_MAIL}`}>
              {IYK_MAIL}
            </a>
          </p>
        </UI.Pixelation>
        <Scripts />
      </body>
    </html>
  )
}

const PostcardError = () => {
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    return (
      <div className="flex flex-col items-center">
        <h1 className="text-lg">
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </div>
    )
  }

  if (error instanceof Error) {
    return (
      <div className="flex flex-col items-center">
        <h1 className="text-lg">Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre className="text-xs">{error.stack}</pre>
      </div>
    )
  }

  return <h1>Unknown Error</h1>
}
